import { fileUrl } from "@/lib/api";

export const BUSES = [
  { id: "footage", name: "Dialogue / Picture", color: "#0284C7" },
  { id: "audio", name: "Music & Audio", color: "#0D9488" },
  { id: "sfx", name: "SFX / Foley", color: "#8B5CF6" },
  { id: "score", name: "Score", color: "#D97706" },
];
export const DEFAULT_BUS = { gain: 0, pan: 0, low: 0, mid: 0, high: 0, threshold: 0, ratio: 1, reverb: 0, mute: false, solo: false };
export const DEFAULT_DUCK = { on: false, depth: -10, attack: 0.15, release: 0.6, buses: ["audio", "score"], regions: [] };
export const defaultMixer = () => ({ master: 0, duck: { ...DEFAULT_DUCK }, buses: Object.fromEntries(BUSES.map((b) => [b.id, { ...DEFAULT_BUS }])) });
export const mergeMixer = (m = {}) => {
  const d = defaultMixer();
  return { master: m.master ?? 0, duck: { ...DEFAULT_DUCK, ...(m.duck || {}) }, buses: Object.fromEntries(BUSES.map((b) => [b.id, { ...d.buses[b.id], ...(m.buses?.[b.id] || {}) }])) };
};
export const trackBus = (track) => (track === "video" ? "footage" : track);
const dbToGain = (db) => (db <= -60 ? 0 : Math.pow(10, db / 20));

function impulse(ctx, sec = 2.6) {
  const len = Math.floor(ctx.sampleRate * sec);
  const b = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = b.getChannelData(ch);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3);
  }
  return b;
}

export function buildGraph(ctx) {
  const master = ctx.createGain();
  const reverb = ctx.createConvolver();
  reverb.buffer = impulse(ctx);
  reverb.connect(master);
  const buses = {};
  BUSES.forEach(({ id }) => {
    const low = ctx.createBiquadFilter(); low.type = "lowshelf"; low.frequency.value = 200;
    const mid = ctx.createBiquadFilter(); mid.type = "peaking"; mid.frequency.value = 1000; mid.Q.value = 0.9;
    const high = ctx.createBiquadFilter(); high.type = "highshelf"; high.frequency.value = 5000;
    const comp = ctx.createDynamicsCompressor();
    const gain = ctx.createGain(), duck = ctx.createGain(), pan = ctx.createStereoPanner(), send = ctx.createGain();
    low.connect(mid); mid.connect(high); high.connect(comp); comp.connect(gain); gain.connect(duck); duck.connect(pan);
    pan.connect(master); pan.connect(send); send.connect(reverb);
    buses[id] = { input: low, low, mid, high, comp, gain, duck, pan, send };
  });
  return { master, buses };
}

export function applyMixer(graph, mixer) {
  const anySolo = BUSES.some((b) => mixer.buses[b.id].solo);
  BUSES.forEach(({ id }) => {
    const p = mixer.buses[id], n = graph.buses[id];
    n.low.gain.value = p.low; n.mid.gain.value = p.mid; n.high.gain.value = p.high;
    n.comp.threshold.value = p.threshold; n.comp.ratio.value = p.ratio;
    n.gain.gain.value = !p.mute && (!anySolo || p.solo) ? dbToGain(p.gain) : 0;
    n.pan.pan.value = p.pan; n.send.gain.value = p.reverb;
  });
  graph.master.gain.value = dbToGain(mixer.master);
}

const duckPoints = (d) => {
  const g = dbToGain(d.depth);
  return d.regions.flatMap(([s, e]) => [[Math.max(0, s - d.attack), 1], [s, g], [e, g], [e + d.release, 1]]);
};
export function duckAt(d, t) {
  if (!d?.on) return 1;
  const pts = duckPoints(d);
  const j = pts.findIndex(([pt]) => pt > t);
  if (j <= 0) return 1;
  const [t0, v0] = pts[j - 1], [t1, v1] = pts[j];
  return v0 + ((v1 - v0) * (t - t0)) / Math.max(1e-6, t1 - t0);
}
export function scheduleDuck(graph, mixer, from, t0) {
  const d = mixer.duck;
  BUSES.forEach(({ id }) => {
    const p = graph.buses[id].duck.gain;
    p.cancelScheduledValues(0);
    if (!d?.on || !d.buses.includes(id)) return p.setValueAtTime(1, t0);
    p.setValueAtTime(duckAt(d, from), t0);
    duckPoints(d).filter(([t]) => t > from).forEach(([t, v]) => p.linearRampToValueAtTime(v, t0 + t - from));
  });
}

export class AudioEngine {
  buffers = new Map();
  ready = new Map();
  sources = [];
  mixer = defaultMixer();

  ensure() {
    if (this.ctx) return this.ctx;
    const ctx = (this.ctx = new (window.AudioContext || window.webkitAudioContext)());
    this.graph = buildGraph(ctx);
    const split = ctx.createChannelSplitter(2);
    this.meters = [ctx.createAnalyser(), ctx.createAnalyser()];
    this.meters.forEach((a, i) => { a.fftSize = 1024; split.connect(a, i); });
    this.graph.master.connect(ctx.destination);
    this.graph.master.connect(split);
    applyMixer(this.graph, this.mixer);
    return ctx;
  }

  async resume() { this.ensure(); if (this.ctx.state === "suspended") await this.ctx.resume(); }

  setMixer(m) {
    const duckChanged = m.duck !== this.mixer.duck;
    this.mixer = m;
    if (!this.graph) return;
    applyMixer(this.graph, m);
    if (duckChanged) this.syncDuck();
  }

  syncDuck() {
    const p = this.playing;
    if (p) scheduleDuck(this.graph, this.mixer, p.from + this.ctx.currentTime - p.t0, this.ctx.currentTime);
    else scheduleDuck(this.graph, { duck: { on: false } }, 0, this.ctx.currentTime);
  }

  attachMedia(el) {
    this.ensure();
    if (!el._clcNode) {
      el._clcNode = this.ctx.createMediaElementSource(el);
      el._clcGain = this.ctx.createGain();
      el._clcNode.connect(el._clcGain);
      el._clcGain.connect(this.graph.buses.footage.input);
    }
  }

  setElementGain(el, g) {
    if (!el?._clcGain || Math.abs((el._clcLevel ?? 1) - g) < 0.002) return;
    el._clcLevel = g;
    el._clcGain.gain.setTargetAtTime(g, this.ctx.currentTime, 0.015);
  }

  async load(id) {
    if (this.ready.has(id)) return this.ready.get(id);
    if (!this.buffers.has(id)) {
      const p = (async () => {
        const r = await fetch(fileUrl(id), { credentials: "include" });
        if (!r.ok) throw new Error(`Load failed (${r.status})`);
        const ab = await r.arrayBuffer();
        this.ensure();
        const buf = await this.ctx.decodeAudioData(ab);
        this.ready.set(id, buf);
        return buf;
      })();
      p.catch(() => this.buffers.delete(id));
      this.buffers.set(id, p);
    }
    return this.buffers.get(id);
  }

  playClips(clips, from) {
    this.stopClips();
    const now = this.ctx.currentTime + 0.03;
    this.playing = { from, t0: now };
    scheduleDuck(this.graph, this.mixer, from, now);
    clips.forEach((c) => {
      const buf = this.ready.get(c.asset_id);
      if (!buf || !this.graph.buses[trackBus(c.track)]) return;
      const inn = c.in || 0, len = c.len ?? buf.duration - inn;
      if (c.start + len <= from) return;
      const offset = Math.max(0, from - c.start);
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.connect(this.graph.buses[trackBus(c.track)].input);
      src.start(now + Math.max(0, c.start - from), inn + offset, len - offset);
      this.sources.push(src);
    });
  }

  stopClips() {
    this.playing = null;
    if (this.graph) this.syncDuck();
    this.sources.forEach((s) => { try { s.stop(); } catch (_) { /* already stopped */ } });
    this.sources = [];
  }

  peaks() {
    if (!this.meters) return [0, 0];
    const d = new Float32Array(1024);
    return this.meters.map((a) => { a.getFloatTimeDomainData(d); let m = 0; for (const v of d) m = Math.max(m, Math.abs(v)); return m; });
  }
}

export async function renderMix(clips, buffers, mixer, duration) {
  const sr = 44100;
  const ctx = new OfflineAudioContext(2, Math.ceil(sr * duration), sr);
  const g = buildGraph(ctx);
  applyMixer(g, mixer);
  scheduleDuck(g, mixer, 0, 0);
  g.master.connect(ctx.destination);
  clips.forEach((c) => {
    const buf = buffers.get(c.asset_id);
    if (!buf || !g.buses[trackBus(c.track)]) return;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(g.buses[trackBus(c.track)].input);
    if (c.len) src.start(c.start, c.in || 0, c.len);
    else src.start(c.start, c.in || 0);
  });
  return ctx.startRendering();
}

export function encodeWav(buffer) {
  const ch = buffer.numberOfChannels, len = buffer.length, sr = buffer.sampleRate;
  const view = new DataView(new ArrayBuffer(44 + len * ch * 2));
  const str = (o, s) => [...s].forEach((c, i) => view.setUint8(o + i, c.charCodeAt(0)));
  str(0, "RIFF"); view.setUint32(4, 36 + len * ch * 2, true); str(8, "WAVE"); str(12, "fmt ");
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, ch, true);
  view.setUint32(24, sr, true); view.setUint32(28, sr * ch * 2, true); view.setUint16(32, ch * 2, true);
  view.setUint16(34, 16, true); str(36, "data"); view.setUint32(40, len * ch * 2, true);
  const data = [...Array(ch)].map((_, i) => buffer.getChannelData(i));
  let o = 44;
  for (let i = 0; i < len; i++)
    for (let c = 0; c < ch; c++) { const s = Math.max(-1, Math.min(1, data[c][i])); view.setInt16(o, s < 0 ? s * 0x8000 : s * 0x7fff, true); o += 2; }
  return new Blob([view], { type: "audio/wav" });
}
